/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { TenantDetails } from 'app/page-modules/tenant-management/models';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { PreviewEnvironment, EnvironmentLogin } from 'app/shared-data/models';
import { LoadableState } from '@opengamma/ui';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import { AuthService } from 'app/user-auth/services/auth.service';
import { UserDetails } from 'app/user-auth/models';
import { startWith, map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'og-environment-login',
  templateUrl: './environment-login.component.html',
  styleUrls: ['./environment-login.component.scss'],
  standalone: false
})
export class EnvironmentLoginComponent implements OnInit {
  private static readonly MAXIMUM_TENANT_NAME_LENGTH = 25;

  readonly dateTimeFormat = DateTime.DATETIME_MED;

  environmentLoginFormGroup: FormGroup;

  // Original input
  private inputTenants: LoadableState<TenantDetails[]>;
  private inputPreviewEnvironments: LoadableState<PreviewEnvironment[]>;

  tenants: TenantDetails[] | undefined;
  previewEnvironments: PreviewEnvironment[] | undefined;

  tenantsFiltered$: Observable<TenantDetails[]>;

  @Input() userDetails: UserDetails;
  @Output() onLoginClicked = new EventEmitter<EnvironmentLogin>();

  @Input() set allTenantsLoadable(tenantsLoadable: LoadableState<TenantDetails[]>) {
    this.inputTenants = tenantsLoadable;
    if (tenantsLoadable.data) {
      this.tenants = this.sortedFilteredTenants(tenantsLoadable.data);
    } else {
      this.tenants = undefined;
    }
  }

  get allTenantsLoadable(): LoadableState<TenantDetails[]> {
    return this.inputTenants;
  }

  @Input() set previewEnvironmentsLoadable(
    previewEnvironmentsLoadable: LoadableState<PreviewEnvironment[]>
  ) {
    this.inputPreviewEnvironments = previewEnvironmentsLoadable;
    if (previewEnvironmentsLoadable.data) {
      this.previewEnvironments = this.sortedEnvironments(previewEnvironmentsLoadable.data);
    } else {
      this.previewEnvironments = undefined;
    }
  }

  get previewEnvironmentsLoadable(): LoadableState<PreviewEnvironment[]> {
    return this.inputPreviewEnvironments;
  }

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.environmentLoginFormGroup = new FormGroup({
      organizationId: new FormControl('', Validators.required),
      previewEnvironmentName: new FormControl('')
    });
    this.tenantsFiltered$ = this.organizationId.valueChanges.pipe(
      startWith(''),
      map(value => this.filterTenantsByInput(value))
    );
  }

  loginToTenant(): void {
    const tenant = this.getSelectedTenant();
    const previewEnvironment = this.getSelectedPreviewEnvironment();
    this.onLoginClicked.next({
      tenantDetails: tenant,
      previewEnvironment: previewEnvironment
    });
  }

  private getSelectedTenant(): TenantDetails {
    const organizationId = this.organizationId.value;
    const tenantData = this.allTenantsLoadable.data;
    // button is disabled if data is not available
    // eslint-disable-next-line
    return tenantData!!.find(item => item.organizationId === organizationId)!;
  }

  private getSelectedPreviewEnvironment(): string | undefined {
    const environmentName = this.previewEnvironmentName.value;
    return environmentName === '' ? undefined : environmentName;
  }

  /**
   * @param tenants a list of tenants
   * @return tenants the logged in user has access to, sorted by organization ID
   */
  private sortedFilteredTenants(tenants: TenantDetails[]): TenantDetails[] {
    const allowedTenants = this.filterAllowedTenants(tenants);
    return this.sortedTenants(allowedTenants);
  }

  private sortedTenants(tenants: TenantDetails[]): TenantDetails[] {
    return _.sortBy(tenants, tenant => tenant.organizationId);
  }

  private filterAllowedTenants(tenants: TenantDetails[]): TenantDetails[] {
    return tenants
      .filter(tenantDetails => tenantDetails.status !== 'disabled')
      .filter(tenantDetails => this.authService.canLoginToTenant(this.userDetails, tenantDetails));
  }

  /**
   * @param environments, sorted by modification date, most recent first
   */
  sortedEnvironments(environments: PreviewEnvironment[]): PreviewEnvironment[] {
    return _.sortBy(environments, env => env.modificationInstant).reverse();
  }

  get organizationId(): AbstractControl {
    return this.environmentLoginFormGroup.get('organizationId')!;
  }

  get previewEnvironmentName(): AbstractControl {
    return this.environmentLoginFormGroup.get('previewEnvironmentName')!;
  }

  /**
   * @param name tenant name
   * @return the arg if it's <= {@link MAXIMUM_TENANT_NAME_LENGTH} chars, else the trimmed name followed by an ellipsis
   */
  trimTenantName(name: string): string {
    return name.length <= EnvironmentLoginComponent.MAXIMUM_TENANT_NAME_LENGTH
      ? name
      : name.substring(0, EnvironmentLoginComponent.MAXIMUM_TENANT_NAME_LENGTH) + '…';
  }

  private filterTenantsByInput(input: string): TenantDetails[] {
    if (this.tenants) {
      return this.tenants.filter(tenant => {
        const searchableTenantName = tenant.name.concat(tenant.organizationId).toLowerCase();
        return searchableTenantName.includes(input.toLowerCase());
      });
    }
    return [];
  }
}
